import { Component, Prop, Vue } from 'vue-property-decorator';
import { isEqual } from 'lodash';
import { NOTIFICATION } from '@/constants';
import { formatDateToDisplay, displayPrizeType, toTreeOptionsByPeriods, extractQuarterFromName } from '@/helpers';
import { RewardRequestDTO, RewardPeriodExportData, TreeOption, RewardPeriodDTO } from '@/models';
import { fetchAllRewardRequests } from '@/services';
import RewardPeriodsExport from '@/services/reward-periods-export';
import TreeFilter from '@/components/common/tree-filter/TreeFilter.vue';

@Component({
  components: {
    TreeFilter,
    RewardPeriodsExport
  }
})
export default class RewardPeriodExport extends Vue {
  @Prop({ default: [] }) private rewardPeriods: RewardPeriodDTO[];

  private modalShown = false;
  private isFetchingData = false;
  private exportData: RewardPeriodExportData[] = [];
  private selectedPeriods: string[] = [];
  private isExporting = false;
  private periods: string[] = [];

  get periodsOptions(): TreeOption[] {
    const sortedData = this.rewardPeriods.sort((a, b) => {
      const firstDate = new Date(a.startDate || '');
      const secondDate = new Date(b.startDate || '');
      return firstDate.getTime() - secondDate.getTime();
    });
    this.periods = sortedData.map(({ name }) => extractQuarterFromName(name)).filter(String);
    this.selectedPeriods = this.periods;
    return toTreeOptionsByPeriods(this.periods);
  }

  get filteredExportData(): RewardPeriodExportData[] {
    if (this.selectedPeriods.length) {
      return this.exportData.filter(d => this.selectedPeriods.includes(d.period));
    }
    return this.exportData;
  }

  get isDisabled(): boolean {
    return this.isExporting || !this.exportData?.length;
  }

  mounted(): void {
    this.isFetchingData = true;
    fetchAllRewardRequests()
      .then(({ data }) => {
        this.exportData = this.toExportData(data);
      })
      .finally(() => {
        this.isFetchingData = false;
      });
  }

  toExportData(rewardRequests: RewardRequestDTO[]): RewardPeriodExportData[] {
    return rewardRequests.map(rr => {
      const { rewardItem, user, reason, cost, actualPrice, status, createdAt, updatedAt } = rr || {};
      const { fullName, code } = user || {};
      const { prize, estimatedPrice } = rewardItem || {};
      const { type, name } = prize || {};
      const requestPeriodName = this.rewardPeriods.find(
        ({ rewardItems }) => rewardItems?.filter(ri => ri.id === rewardItem?.id).length
      )?.name;
      return {
        user: `${fullName} - ${code}`,
        item: name,
        cost: cost || 0,
        estimatedPrice,
        actualPrice: actualPrice || 0,
        createdAt: formatDateToDisplay(createdAt || ''),
        resolvedAt: formatDateToDisplay(updatedAt || ''),
        status,
        reason,
        category: displayPrizeType(type),
        period: extractQuarterFromName(requestPeriodName || '')
      };
    });
  }

  showModal(): void {
    if (!this.isDisabled) {
      this.modalShown = true;
    }
  }

  async onModalConfirm(): Promise<void> {
    this.isExporting = true;
    const rewardPeriodsExport = new RewardPeriodsExport(this.filteredExportData);
    rewardPeriodsExport.generateXLSXFile().finally(() => {
      this.isExporting = false;
      const notificationInfo = {
        showing: true,
        type: NOTIFICATION.TYPE.success,
        title: NOTIFICATION.TEXT.exportDataSuccess,
        content: [],
        icon: NOTIFICATION.ICON.success,
        multiline: false,
        height: 0
      };
      this.$store.commit('notification/setNotification', notificationInfo);
    });
  }

  onModalCancel(): void {
    this.modalShown = false;
  }

  onSelectAll(): void {
    this.selectedPeriods = isEqual(this.selectedPeriods, this.periods) ? [] : this.periods;
  }
}
